/**
 * Full-screen image preview (lightbox) — the React counterpart of the
 * viewer Vue gets for free from Naive UI's `n-image` (`HkImage.vue`):
 * zoom in/out, rotate left/right, flip, reset, download, close.
 *
 * Renders through a portal so it escapes any table / modal / slide-over
 * overflow clipping; closes on Escape and on a backdrop click. The chrome
 * is fixed-dark in BOTH themes (`--color-lightbox-*`) — it sits over the
 * image, not over the page.
 *
 * Generalised from CEO Pricing's lightbox so Orders / Staged Orders line
 * items and CEO thumbnails share ONE viewer. Mount it once through
 * `ImagePreviewProvider` and open it with `useImagePreviewOptional()`.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import IconClose from '../../icons/IconClose';
import IconDownload from '../../icons/IconDownload';
import IconFlipHorizontal from '../../icons/IconFlipHorizontal';
import IconFlipVertical from '../../icons/IconFlipVertical';
import IconResetView from '../../icons/IconResetView';
import IconRotateLeft from '../../icons/IconRotateLeft';
import IconRotateRight from '../../icons/IconRotateRight';
import IconZoomIn from '../../icons/IconZoomIn';
import IconZoomOut from '../../icons/IconZoomOut';

export interface PreviewImage {
  url: string;
  /** Caption / alt text / download filename — typically the SKU. */
  label: string;
}

interface Props {
  image: PreviewImage | null;
  onClose: () => void;
}

/** Tabbable elements inside the lightbox, in DOM order (§15 keyboard). */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Above every other layer: slide-overs (1100/1200) and modals (2000) can
// all host a previewable thumbnail.
const OVERLAY_CLASS =
  'fixed inset-0 w-full h-full z-[3000] flex flex-col items-center justify-center ' +
  'bg-[var(--color-lightbox-scrim)] p-6';
// pointer-events-none so clicks in the empty area around the image fall
// through to the full-screen backdrop <button> (which closes); the image
// itself re-enables pointer events via pointer-events-auto.
const IMG_WRAP_CLASS =
  'flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden pointer-events-none';
const IMG_CLASS =
  'max-w-[90vw] max-h-[78vh] object-contain select-none transition-transform duration-150';
const TOOLBAR_CLASS =
  'mt-4 flex items-center gap-1 px-2 py-1.5 rounded-full ' +
  'bg-[var(--color-lightbox-toolbar-bg)] border border-solid border-[var(--color-lightbox-toolbar-border)]';
const TOOL_BTN_CLASS =
  'w-9 h-9 flex items-center justify-center rounded-full bg-transparent border-0 ' +
  'text-[var(--color-lightbox-fg)] hover:bg-[var(--color-lightbox-btn-hover)] cursor-pointer ' +
  'transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
const TITLE_CLASS =
  'absolute top-5 left-6 text-[var(--color-lightbox-fg)] text-sm font-[var(--font-weight-medium)]';
const BACKDROP_CLASS =
  'absolute inset-0 w-full h-full bg-transparent border-0 cursor-default p-0 m-0 z-0';
const IMG_HOST_CLASS =
  'bg-transparent border-0 p-0 m-0 cursor-default inline-flex pointer-events-auto';
const DOWNLOAD_FALLBACK_NAME = 'image';
const ICON_SIZE = 18;

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const ROTATE_STEP_DEG = 90;

export function ImagePreview({ image, onClose }: Readonly<Props>): React.ReactElement | null {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [flipY, setFlipY] = useState(false);

  const reset = useCallback((): void => {
    setScale(1);
    setRotate(0);
    setFlipX(false);
    setFlipY(false);
  }, []);

  // Reset all transforms whenever a new image opens.
  useEffect(() => {
    if (image !== null) reset();
  }, [image, reset]);

  useEffect(() => {
    if (image === null) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // `<dialog open>` is non-modal — it gets no browser focus trap, so Tab
      // would walk into the page behind the scrim while `aria-modal` tells
      // assistive tech the background is inert. Cycle focus by hand instead.
      // (`showModal()` would do this natively but is unimplemented in jsdom,
      // so it cannot be covered by a test.)
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (dialog === null) return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
        (el) => !el.hasAttribute('disabled') && el.tabIndex >= 0,
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      // Focus can end up outside the dialog without a Tab: disabling the
      // focused control (zooming to a clamp) drops focus to <body>. Pull it
      // back in rather than letting the next Tab escape behind the scrim.
      if (active === null || !dialog.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [image, onClose]);

  // Focus the dialog on open and restore focus to the trigger on close, so
  // keyboard users don't keep tabbing the page behind the overlay.
  useEffect(() => {
    if (image === null) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, [image]);

  if (image === null) return null;

  const zoomIn = (): void => setScale((s) => Math.min(ZOOM_MAX, s + ZOOM_STEP));
  const zoomOut = (): void => setScale((s) => Math.max(ZOOM_MIN, s - ZOOM_STEP));
  const rotateLeft = (): void => setRotate((r) => r - ROTATE_STEP_DEG);
  const rotateRight = (): void => setRotate((r) => r + ROTATE_STEP_DEG);

  const download = (): void => {
    const a = document.createElement('a');
    a.href = image.url;
    a.download = image.label || DOWNLOAD_FALLBACK_NAME;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const transform = `scale(${scale * (flipX ? -1 : 1)}, ${scale * (flipY ? -1 : 1)}) rotate(${rotate}deg)`;
  const imageLabel = t('common.imagePreview.image', 'Image');
  const dialogLabel = image.label ? `${image.label} — ${imageLabel}` : imageLabel;

  const tools: ReadonlyArray<{
    key: string;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    icon: React.ReactNode;
  }> = [
    { key: 'zoomOut', label: t('common.imagePreview.zoomOut', 'Zoom out'), onClick: zoomOut, disabled: scale <= ZOOM_MIN, icon: <IconZoomOut size={ICON_SIZE} /> },
    { key: 'zoomIn', label: t('common.imagePreview.zoomIn', 'Zoom in'), onClick: zoomIn, disabled: scale >= ZOOM_MAX, icon: <IconZoomIn size={ICON_SIZE} /> },
    { key: 'rotateLeft', label: t('common.imagePreview.rotateLeft', 'Rotate left'), onClick: rotateLeft, icon: <IconRotateLeft size={ICON_SIZE} /> },
    { key: 'rotateRight', label: t('common.imagePreview.rotateRight', 'Rotate right'), onClick: rotateRight, icon: <IconRotateRight size={ICON_SIZE} /> },
    { key: 'flipHorizontal', label: t('common.imagePreview.flipHorizontal', 'Flip horizontal'), onClick: () => setFlipX((v) => !v), icon: <IconFlipHorizontal size={ICON_SIZE} /> },
    { key: 'flipVertical', label: t('common.imagePreview.flipVertical', 'Flip vertical'), onClick: () => setFlipY((v) => !v), icon: <IconFlipVertical size={ICON_SIZE} /> },
    { key: 'reset', label: t('common.imagePreview.reset', 'Reset'), onClick: reset, icon: <IconResetView size={ICON_SIZE} /> },
    { key: 'download', label: t('common.imagePreview.download', 'Download'), onClick: download, icon: <IconDownload size={ICON_SIZE} /> },
    { key: 'close', label: t('common.imagePreview.close', 'Close'), onClick: onClose, icon: <IconClose size={ICON_SIZE} /> },
  ];

  return createPortal(
    <dialog
      ref={dialogRef}
      open
      className={`rw-native-dialog ${OVERLAY_CLASS}`}
      aria-modal="true"
      aria-label={dialogLabel}
      tabIndex={-1}
    >
      {/* Backdrop: a real <button> (S6819) covers the full overlay so a click
          outside the image/toolbar closes. Pointer-only: tabIndex={-1} keeps
          this invisible full-viewport element out of the tab order, so Tab
          (and the focus recovery above) lands on a control the user can SEE.
          Closing by keyboard is the toolbar's Close button and Escape. */}
      <button
        type="button"
        tabIndex={-1}
        className={BACKDROP_CLASS}
        onClick={onClose}
        aria-label={t('common.imagePreview.closePreview', 'Close image preview')}
      />
      {image.label ? <span className={`${TITLE_CLASS} z-[1]`}>{image.label}</span> : null}

      <div className={`${IMG_WRAP_CLASS} z-[1]`}>
        {/* Exists only so stopPropagation lives on an interactive element
            (S6847); activating it does nothing, so it is pointer-only —
            otherwise it would be the first tab stop, ahead of the toolbar. */}
        <button
          type="button"
          tabIndex={-1}
          className={IMG_HOST_CLASS}
          onClick={(e) => e.stopPropagation()}
          aria-label={imageLabel}
        >
          <img
            src={image.url}
            alt={image.label}
            className={IMG_CLASS}
            style={{ transform }}
            draggable={false}
          />
        </button>
      </div>

      <div
        className={`${TOOLBAR_CLASS} z-[1]`}
        role="toolbar"
        aria-label={t('common.imagePreview.controls', 'Image controls')}
      >
        {tools.map((tool) => (
          <button
            key={tool.key}
            type="button"
            className={TOOL_BTN_CLASS}
            onClick={tool.onClick}
            disabled={tool.disabled}
            title={tool.label}
            aria-label={tool.label}
          >
            {tool.icon}
          </button>
        ))}
      </div>
    </dialog>,
    document.body,
  );
}

export default ImagePreview;
